import { enums } from 'protoswipe-shared'
import { shuffle } from '@/lib/helpers'

const state = {
	loading: false,
	data: null,
	index: 0,
	votes: [],
	lastReference: null
}

const getters = {
	exists({ data }) {
		return data !== null
	},
	campaign({ data }, { exists }) {
		return exists ? data.campaign : null
	},
	numSubmissions({ data }, { exists }) {
		return exists ? data.submissions.length : 0
	},
	canVote({ votes }, { numSubmissions }) {
		return votes.length < numSubmissions
	},
	currentSubmission({ data, index }, { exists, campaign }) {
		return exists ? { ...data.submissions[index], campaign } : null
	},
	submissionsLeft({ data, index }) {
		if (!data) return 1
		return data.submissions.length - index
	},
	progress({ data, index }) {
		if (!data) return 1
		return index / data.submissions.length
	}
}

const mutations = {
	setSession(state, data) {
		state.data = data
		// Shuffle submissions
		state.data.submissions = shuffle(data.submissions)
		state.votes = []
		state.index = 0
	},
	endSession(state, reference) {
		state.data = null
		if (reference) state.lastReference = reference
	},
	setLoading(state, loading = true) {
		state.loading = loading
	},
	addVote(state, { id, liked }) {
		state.votes.push({ id, liked })
		state.index++
	}
}

const actions = {
	async start({ dispatch, commit, getters, state }, { force = false } = {}) {
		// Do nothing if a session is loading
		if (state.loading) return false
		// Jump straight to voting if session in progress
		else if (getters.exists && !force) return true

		// Define toast shortcut function
		const toast = (message, severity = 'info') => {
			return dispatch('toast/push', { message, severity }, { root: true })
		}

		// Start loading
		commit('setLoading')

		try {
			// Captcha challenge
			const token = await this._vm.$recaptcha(enums.RecaptchaAction.vote)

			// Create a new session
			const endpoint = '/sessions/new'
			const session = await dispatch(
				'api/post',
				{ endpoint, body: JSON.stringify({ token }) },
				{ root: true }
			).then(res => res.json())
			commit('setSession', session)

			// Send user to voting page
			commit('setLoading', false)
			return true
		} catch (err) {
			// Show relevant error
			if (err.status !== 500 && err.message) {
				toast(err.message, 'warning')
			} else {
				toast('Something went wrong. Please try again later.', 'error')
			}

			// Return false
			commit('setLoading', false)
			return false
		}
	},
	addVote({ getters, commit }, { id, liked }) {
		if (getters.exists && getters.canVote) {
			commit('addVote', { id, liked })
		}
	},
	async end({ state, getters, commit, dispatch }) {
		if (getters.exists && !state.loading) {
			// Start loading
			commit('setLoading')
			let success = true
			let error = null

			// Submit votes
			try {
				// Collate likes
				const likes = state.votes.reduce((acc, vote) => {
					return vote.liked ? [...acc, vote.id] : acc
				}, [])

				// Send back to server
				const { reference } = state.data
				await dispatch(
					'api/post',
					{
						endpoint: `/sessions/${reference}`,
						body: JSON.stringify({ likes })
					},
					{ root: true }
				)

				// Stop loading, delete current session and return reference
				commit('endSession', reference)
			} catch (err) {
				// Check error type
				const is40X = err.status >= 400 && err.status < 500

				// End the session on 40X errors
				if (is40X) commit('endSession')

				// Update error message
				success = false
				error =
					is40X && err.message
						? err.message
						: 'Something went wrong. Please try again later.'
			}

			// Stop loading and return success and error states
			commit('setLoading', false)
			return { success, error }
		}
	}
}

export default {
	state,
	getters,
	mutations,
	actions,
	namespaced: true
}
